import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  X,
  Plus,
  Rocket,
  AlertTriangle,
  Loader2,
  ListOrdered,
  Trash2,
  Eye,
} from "lucide-react";
import type { AiAgent } from "@shared/schema";
import { useAiAgentsSurface } from "@/pages/ai-agents-surface";

// ── Reference-data shapes (mirror the single-run wizard) ─────────────────────
interface StateRow {
  state: string;
  stateAbbrev: string;
  cityCount: number;
}
interface CityRow {
  id: string;
  city: string;
  state: string;
  stateAbbrev: string;
  population: number | null;
  rank: number | null;
}
interface BusinessType {
  id: string;
  name: string;
}
interface SelectedCity {
  state: string;
  stateAbbrev: string;
  city: string;
  population: number | null;
}
interface OutreachCampaign {
  id: string;
  name: string;
  type: "drip" | "mass";
  status?: string;
}
interface OutreachUser {
  id: string;
  name: string;
}
interface OutreachMeta {
  campaigns: { drip: OutreachCampaign[]; mass: OutreachCampaign[] };
  hasColdDomain: boolean;
  users: OutreachUser[];
}

// One scoped run that will be chained into the queue.
interface ScopeEntry {
  id: string;
  businessTypes: string[];
  cities: SelectedCity[];
}

const fmt = (n: number) => n.toLocaleString("en-US");
const cityKey = (c: { stateAbbrev: string; city: string }) =>
  `${c.stateAbbrev}|${c.city}`;
const NONE = "__none__";

// =============================================================================
// Run Queue Builder
//
// Operator assembles an ORDERED batch of scoped runs (each its own business
// types × cities). Launching runs them sequentially & unattended — the next
// starts automatically when the prior completes OR exhausts its per-run cap.
// A batch-level default campaign (tenant) is resolved per-cell at enrollment,
// with optional per-business-type overrides that fall back to the default.
//
// This is additive: it does NOT touch the single-run LeadOutreachWizard path.
// Both surfaces use it via useAiAgentsSurface() — platform imports prospects
// only (no campaign/compose/overrides), tenant enrolls + sends.
// =============================================================================
export function RunQueueBuilder({
  agent,
  open,
  onOpenChange,
}: {
  agent: AiAgent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const { apiBase, enrollsLeads } = useAiAgentsSurface();

  const [queueName, setQueueName] = useState("");
  const [spendCapUsd, setSpendCapUsd] = useState("");
  const [entries, setEntries] = useState<ScopeEntry[]>([]);

  // Outreach defaults (tenant only).
  const [messageSource, setMessageSource] = useState<"existing" | "compose">("existing");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [typeOverrides, setTypeOverrides] = useState<Record<string, string>>({});
  const [visibleUserIds, setVisibleUserIds] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [warmFallbackAck, setWarmFallbackAck] = useState(false);

  useEffect(() => {
    if (!open) {
      setQueueName("");
      setSpendCapUsd("");
      setEntries([]);
      setMessageSource("existing");
      setSelectedCampaignId("");
      setComposeSubject("");
      setComposeBody("");
      setTypeOverrides({});
      setVisibleUserIds([]);
      setUserSearch("");
      setWarmFallbackAck(false);
    }
  }, [open]);

  // ── Reference data ─────────────────────────────────────────────────────────
  const { data: types = [] } = useQuery<BusinessType[]>({
    queryKey: [`${apiBase}/meta/business-types`],
    enabled: open,
  });
  const { data: states = [], isLoading: statesLoading } = useQuery<StateRow[]>({
    queryKey: [`${apiBase}/meta/states`],
    enabled: open,
  });
  const { data: seedStatus } = useQuery<{ ready: boolean; count: number }>({
    queryKey: [`${apiBase}/meta/cities-ready`],
    enabled: open,
    refetchInterval: (query) => {
      const data = query.state.data as { ready: boolean; count: number } | undefined;
      return data?.ready === false ? 3000 : false;
    },
  });
  const seedReady = seedStatus?.ready !== false;
  useEffect(() => {
    if (seedStatus?.ready === true) {
      queryClient.invalidateQueries({ queryKey: [`${apiBase}/meta/states`] });
    }
  }, [seedStatus?.ready, apiBase]);

  const { data: outreachMeta } = useQuery<OutreachMeta>({
    queryKey: [`${apiBase}/meta/outreach`],
    enabled: open && enrollsLeads,
  });
  const allCampaigns = useMemo<OutreachCampaign[]>(
    () => [
      ...(outreachMeta?.campaigns.drip ?? []),
      ...(outreachMeta?.campaigns.mass ?? []),
    ],
    [outreachMeta],
  );
  const wsUsers = outreachMeta?.users ?? [];
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return wsUsers;
    return wsUsers.filter((u) => u.name.toLowerCase().includes(q));
  }, [wsUsers, userSearch]);

  const toggleVisibleUser = (id: string) =>
    setVisibleUserIds((prev) =>
      prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id],
    );

  const addEntry = (entry: Omit<ScopeEntry, "id">) =>
    setEntries((prev) => [
      ...prev,
      { ...entry, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` },
    ]);
  const removeEntry = (id: string) =>
    setEntries((prev) => prev.filter((e) => e.id !== id));

  // Distinct business types across every entry — the keys eligible for an
  // override. An override falls back to the default campaign when unset.
  const distinctBizTypes = useMemo(() => {
    const s = new Set<string>();
    entries.forEach((e) => e.businessTypes.forEach((b) => s.add(b)));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [entries]);

  // Drop overrides whose business type is no longer present in any entry.
  useEffect(() => {
    setTypeOverrides((prev) => {
      const next: Record<string, string> = {};
      for (const k of distinctBizTypes) if (prev[k]) next[k] = prev[k];
      return next;
    });
  }, [distinctBizTypes]);

  // ── Validation ───────────────────────────────────────────────────────────
  const capValue = Number(spendCapUsd);
  const capValid =
    spendCapUsd.trim() !== "" && Number.isFinite(capValue) && capValue > 0;

  const messageValid =
    !enrollsLeads ||
    (messageSource === "existing"
      ? !!selectedCampaignId
      : composeSubject.trim().length > 0 && composeBody.trim().length > 0);

  const needsWarmAck =
    enrollsLeads && !!outreachMeta && outreachMeta.hasColdDomain === false;
  const warmAckOk = !needsWarmAck || warmFallbackAck;

  const totalCities = entries.reduce((n, e) => n + e.cities.length, 0);
  const totalCells = entries.reduce(
    (n, e) => n + e.businessTypes.length * e.cities.length,
    0,
  );

  const canLaunch =
    entries.length > 0 && capValid && messageValid && warmAckOk;

  // The stored campaign kind mirrors the chosen default (tenant). Platform
  // batches defer sending, so the kind is cosmetic — default to "drip".
  const resolvedCampaignType: "drip" | "mass" = !enrollsLeads
    ? "drip"
    : messageSource === "compose"
      ? "drip"
      : allCampaigns.find((c) => c.id === selectedCampaignId)?.type ?? "drip";

  const buildBody = () => {
    const body: Record<string, unknown> = {
      name: queueName.trim() || undefined,
      spendCapUsd: capValue,
      campaignType: resolvedCampaignType,
      visibleUserIds: visibleUserIds.length ? visibleUserIds : undefined,
      runs: entries.map((e) => ({
        scope: {
          businessTypes: e.businessTypes,
          cities: e.cities,
          verifyEmails: true,
        },
      })),
    };
    if (enrollsLeads) {
      if (messageSource === "existing") {
        if (selectedCampaignId) body.defaultCampaignId = selectedCampaignId;
      } else {
        body.defaultComposeSubject = composeSubject.trim();
        body.defaultComposeBody = composeBody.trim();
      }
      const cleaned = Object.fromEntries(
        Object.entries(typeOverrides).filter(([, v]) => v),
      );
      if (Object.keys(cleaned).length) body.typeOverrides = cleaned;
      body.warmFallbackAck = warmFallbackAck;
    }
    return body;
  };

  const launchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `${apiBase}/${agent.id}/batches`, buildBody());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiBase] });
      queryClient.invalidateQueries({ queryKey: [apiBase, agent.id] });
      toast({
        title: "Run queue launched",
        description: `${entries.length} run${entries.length === 1 ? "" : "s"} chained. The next starts automatically as each prior one finishes or hits its cap.`,
      });
      onOpenChange(false);
    },
    onError: (e: any) =>
      toast({
        title: "Could not launch run queue",
        description: e?.message,
        variant: "destructive",
      }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListOrdered className="h-5 w-5" /> New Run Queue — {agent.name}
          </DialogTitle>
          <DialogDescription>
            Chain several scoped runs back-to-back. They run sequentially and
            unattended — the next launches automatically as each prior one
            completes or hits its per-run spend cap.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="space-y-6 py-1">
            {/* Queue meta */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="queue-name">Queue name (optional)</Label>
                <Input
                  id="queue-name"
                  placeholder="e.g. Texas + Florida sweep"
                  value={queueName}
                  onChange={(e) => setQueueName(e.target.value)}
                  data-testid="input-queue-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="queue-cap">Per-run spend cap (USD)</Label>
                <Input
                  id="queue-cap"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="decimal"
                  placeholder="e.g. 20"
                  value={spendCapUsd}
                  onChange={(e) => setSpendCapUsd(e.target.value)}
                  data-testid="input-queue-cap"
                />
                <p className="text-xs text-muted-foreground">
                  Applied to every run. A run auto-pauses the instant its own
                  spend crosses the cap, then the next in the queue launches.
                </p>
              </div>
            </div>

            {/* Scope-entry editor */}
            <ScopeEditor
              apiBase={apiBase}
              types={types}
              states={states}
              statesLoading={statesLoading}
              seedReady={seedReady}
              onAdd={addEntry}
            />

            {/* The queue */}
            <div className="space-y-2">
              <Label>
                Queue ({entries.length} run{entries.length === 1 ? "" : "s"})
              </Label>
              {entries.length === 0 ? (
                <div
                  className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground"
                  data-testid="empty-queue"
                >
                  No runs added yet. Build a scope above and press “Add to queue”.
                </div>
              ) : (
                <div className="space-y-2">
                  {entries.map((e, i) => (
                    <div
                      key={e.id}
                      className="flex items-start justify-between gap-3 rounded-md border p-3"
                      data-testid={`queue-entry-${i}`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <Badge variant="secondary" className="mt-0.5 shrink-0">
                          #{i + 1}
                        </Badge>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {e.businessTypes.join(", ")}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {fmt(e.cities.length)} cities ·{" "}
                            {fmt(e.businessTypes.length * e.cities.length)} searches
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => removeEntry(e.id)}
                        data-testid={`button-remove-entry-${i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground" data-testid="text-queue-totals">
                    Total: {fmt(totalCities)} cities · {fmt(totalCells)} searches across{" "}
                    {entries.length} run{entries.length === 1 ? "" : "s"}.
                  </p>
                </div>
              )}
            </div>

            {/* Default outreach (tenant only) */}
            {enrollsLeads && (
              <div className="space-y-3 rounded-md border p-4">
                <div>
                  <Label>Default outreach for the whole queue</Label>
                  <p className="text-xs text-muted-foreground">
                    Every qualified lead enrolls here unless a per-type override
                    below matches its business type.
                  </p>
                </div>
                <RadioGroup
                  value={messageSource}
                  onValueChange={(v) => setMessageSource(v as "existing" | "compose")}
                  className="space-y-2"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="existing" data-testid="radio-default-existing" />
                    <span className="text-sm">Use an existing campaign</span>
                  </label>
                  {messageSource === "existing" && (
                    <div className="pl-6">
                      <Select
                        value={selectedCampaignId}
                        onValueChange={setSelectedCampaignId}
                      >
                        <SelectTrigger data-testid="select-default-campaign">
                          <SelectValue placeholder="Pick a campaign…" />
                        </SelectTrigger>
                        <SelectContent>
                          {allCampaigns.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name} ({c.type === "drip" ? "drip" : "mass"})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="compose" data-testid="radio-default-compose" />
                    <span className="text-sm">Compose a fresh message (cold drip)</span>
                  </label>
                  {messageSource === "compose" && (
                    <div className="pl-6 space-y-2">
                      <Input
                        placeholder="Subject"
                        value={composeSubject}
                        onChange={(e) => setComposeSubject(e.target.value)}
                        data-testid="input-default-subject"
                      />
                      <textarea
                        className="w-full min-h-[100px] rounded-md border bg-background p-2 text-sm"
                        placeholder="Message body…"
                        value={composeBody}
                        onChange={(e) => setComposeBody(e.target.value)}
                        data-testid="input-default-body"
                      />
                    </div>
                  )}
                </RadioGroup>

                {/* Per-business-type overrides */}
                {distinctBizTypes.length > 0 && (
                  <div className="space-y-2 pt-2 border-t">
                    <Label className="text-xs">
                      Per-business-type overrides (optional)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Send specific business types to a different existing
                      campaign. Anything left on “Use default” falls back to the
                      default above.
                    </p>
                    <div className="space-y-2">
                      {distinctBizTypes.map((bt) => (
                        <div
                          key={bt}
                          className="grid grid-cols-2 items-center gap-3"
                          data-testid={`override-row-${bt}`}
                        >
                          <span className="text-sm truncate">{bt}</span>
                          <Select
                            value={typeOverrides[bt] ?? NONE}
                            onValueChange={(v) =>
                              setTypeOverrides((prev) => {
                                const next = { ...prev };
                                if (v === NONE) delete next[bt];
                                else next[bt] = v;
                                return next;
                              })
                            }
                          >
                            <SelectTrigger data-testid={`select-override-${bt}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>Use default</SelectItem>
                              {allCampaigns.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.name} ({c.type === "drip" ? "drip" : "mass"})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reply-thread visibility (both surfaces) */}
            <div className="space-y-2 rounded-md border p-4">
              <Label className="flex items-center gap-2">
                <Eye className="h-4 w-4" /> Reply-thread visibility (optional)
              </Label>
              <p className="text-xs text-muted-foreground">
                Restrict who sees inbound replies for leads sourced by this
                queue. Leave empty for no extra restriction.
              </p>
              {enrollsLeads && wsUsers.length > 0 ? (
                <>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      placeholder="Search teammates…"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      data-testid="input-user-search"
                    />
                  </div>
                  <ScrollArea className="h-[140px] rounded-md border p-1">
                    <div className="space-y-0.5">
                      {filteredUsers.map((u) => (
                        <label
                          key={u.id}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 hover-elevate cursor-pointer"
                          data-testid={`option-visible-user-${u.id}`}
                        >
                          <Checkbox
                            checked={visibleUserIds.includes(u.id)}
                            onCheckedChange={() => toggleVisibleUser(u.id)}
                          />
                          <span className="text-sm">{u.name}</span>
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No teammates available to restrict to.
                </p>
              )}
            </div>

            {/* Warm-fallback acknowledgement (tenant, no verified cold domain) */}
            {needsWarmAck && (
              <div
                className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                data-testid="warning-warm-fallback"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    This workspace has no verified cold sending domain. Cold
                    outreach will fall back to a connected warm mailbox, which
                    can affect that inbox's reputation.
                  </span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer pl-6">
                  <Checkbox
                    checked={warmFallbackAck}
                    onCheckedChange={(c) => setWarmFallbackAck(!!c)}
                    data-testid="checkbox-warm-fallback-ack"
                  />
                  <span className="text-xs">
                    I understand and want to send via a warm mailbox.
                  </span>
                </label>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex items-center justify-between sm:justify-between border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => launchMutation.mutate()}
            disabled={!canLaunch || launchMutation.isPending}
            data-testid="button-launch-queue"
          >
            <Rocket className="h-4 w-4 mr-2" />
            {launchMutation.isPending
              ? "Launching…"
              : `Launch queue (${entries.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Scope editor: build a single scoped run, then "Add to queue" ─────────────
function ScopeEditor({
  apiBase,
  types,
  states,
  statesLoading,
  seedReady,
  onAdd,
}: {
  apiBase: string;
  types: BusinessType[];
  states: StateRow[];
  statesLoading: boolean;
  seedReady: boolean;
  onAdd: (entry: { businessTypes: string[]; cities: SelectedCity[] }) => void;
}) {
  const [businessTypes, setBusinessTypes] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<Record<string, SelectedCity>>({});
  const [activeStates, setActiveStates] = useState<Set<string>>(new Set());
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  const activeStatesSorted = useMemo(() => [...activeStates].sort(), [activeStates]);
  const selectedList = useMemo(() => Object.values(selectedCities), [selectedCities]);

  const { data: cities = [], isFetching: citiesLoading } = useQuery<CityRow[]>({
    queryKey: [`${apiBase}/meta/cities`, activeStatesSorted.join(","), citySearch],
    queryFn: async () => {
      const params = new URLSearchParams({
        states: activeStatesSorted.join(","),
        limit: "1500",
      });
      if (citySearch.trim()) params.set("q", citySearch.trim());
      const res = await fetch(`${apiBase}/meta/cities?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load cities");
      return res.json();
    },
    enabled: activeStatesSorted.length > 0 && seedReady,
  });

  const toggleBusinessType = (name: string) =>
    setBusinessTypes((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );

  const toggleCity = (c: CityRow) => {
    const key = cityKey(c);
    setSelectedCities((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else
        next[key] = {
          state: c.state,
          stateAbbrev: c.stateAbbrev,
          city: c.city,
          population: c.population,
        };
      return next;
    });
  };

  const addAllVisible = () =>
    setSelectedCities((prev) => {
      const next = { ...prev };
      for (const c of cities)
        next[cityKey(c)] = {
          state: c.state,
          stateAbbrev: c.stateAbbrev,
          city: c.city,
          population: c.population,
        };
      return next;
    });

  const reset = () => {
    setBusinessTypes([]);
    setSelectedCities({});
    setActiveStates(new Set());
    setStateSearch("");
    setCitySearch("");
  };

  const canAdd = businessTypes.length > 0 && selectedList.length > 0;

  const filteredStates = states.filter(
    (s) =>
      !stateSearch.trim() ||
      s.state.toLowerCase().includes(stateSearch.trim().toLowerCase()) ||
      s.stateAbbrev.toLowerCase().includes(stateSearch.trim().toLowerCase()),
  );

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-center justify-between">
        <Label>Build a scope entry</Label>
        <Button
          size="sm"
          onClick={() => {
            onAdd({ businessTypes: [...businessTypes], cities: [...selectedList] });
            reset();
          }}
          disabled={!canAdd}
          data-testid="button-add-entry"
        >
          <Plus className="h-4 w-4 mr-1" /> Add to queue
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Business types */}
        <div className="space-y-1.5">
          <Label className="text-xs">Business types</Label>
          <ScrollArea className="h-[220px] rounded-md border p-2">
            <div className="space-y-0.5">
              {types.length === 0 ? (
                <p className="text-xs text-muted-foreground p-1">
                  None defined yet.
                </p>
              ) : (
                types.map((t) => (
                  <label
                    key={t.id}
                    className="flex items-center gap-2 rounded-md p-1.5 hover-elevate cursor-pointer"
                    data-testid={`scope-business-type-${t.id}`}
                  >
                    <Checkbox
                      checked={businessTypes.includes(t.name)}
                      onCheckedChange={() => toggleBusinessType(t.name)}
                    />
                    <span className="text-sm truncate">{t.name}</span>
                  </label>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* States */}
        <div className="space-y-1.5">
          <Label className="text-xs">States</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8 h-9"
              placeholder="Search…"
              value={stateSearch}
              onChange={(e) => setStateSearch(e.target.value)}
              data-testid="input-scope-state-search"
            />
          </div>
          <ScrollArea className="h-[175px] rounded-md border p-1">
            <div className="space-y-0.5">
              {!seedReady ? (
                <div className="flex items-center justify-center h-[150px] text-xs text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Seeding…
                </div>
              ) : statesLoading ? (
                <div className="flex items-center justify-center h-[150px] text-xs text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : (
                filteredStates.map((s) => (
                  <label
                    key={s.stateAbbrev}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover-elevate cursor-pointer"
                    data-testid={`scope-state-${s.stateAbbrev}`}
                  >
                    <Checkbox
                      checked={activeStates.has(s.stateAbbrev)}
                      onCheckedChange={() =>
                        setActiveStates((prev) => {
                          const next = new Set(prev);
                          if (next.has(s.stateAbbrev)) next.delete(s.stateAbbrev);
                          else next.add(s.stateAbbrev);
                          return next;
                        })
                      }
                    />
                    <span className="text-sm truncate">{s.state}</span>
                  </label>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Cities */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Cities</Label>
            {cities.length > 0 && (
              <button
                className="text-xs text-primary hover:underline"
                onClick={addAllVisible}
                data-testid="button-scope-add-all-cities"
              >
                Add all visible
              </button>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8 h-9"
              placeholder="Search cities…"
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              data-testid="input-scope-city-search"
            />
          </div>
          <ScrollArea className="h-[175px] rounded-md border p-1">
            <div className="space-y-0.5">
              {activeStatesSorted.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2">
                  Pick a state to load its cities.
                </p>
              ) : citiesLoading ? (
                <div className="flex items-center justify-center h-[150px] text-xs text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : cities.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2">No cities.</p>
              ) : (
                cities.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover-elevate cursor-pointer"
                    data-testid={`scope-city-${c.id}`}
                  >
                    <Checkbox
                      checked={!!selectedCities[cityKey(c)]}
                      onCheckedChange={() => toggleCity(c)}
                    />
                    <span className="text-sm truncate">
                      {c.city}, {c.stateAbbrev}
                    </span>
                  </label>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Current selection summary + chips */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground" data-testid="text-scope-summary">
          {businessTypes.length} type{businessTypes.length === 1 ? "" : "s"} ·{" "}
          {selectedList.length} cit{selectedList.length === 1 ? "y" : "ies"} ={" "}
          {businessTypes.length * selectedList.length} searches
        </p>
        {selectedList.length > 0 && (
          <button
            className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
            onClick={() => setSelectedCities({})}
            data-testid="button-clear-cities"
          >
            <X className="h-3 w-3" /> Clear cities
          </button>
        )}
      </div>
    </div>
  );
}
